import React from "react";
import Image7 from '../../../Asset/image7.png';
import { 
    FaMoneyBillWave, 
    FaHandHoldingHeart, 
    FaFileInvoice, 
    FaCalendarAlt, 
    FaUsers, 
    FaPhoneAlt, 
    FaEnvelope 
} from "react-icons/fa";
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useColorMode, Box } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const AideFinanciere = () => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const aides = [
        {
            title: "Aide aux personnes âgées (APA)",
            description: "L’APA est une allocation destinée aux personnes âgées de 60 ans et plus en perte d’autonomie. La demande s’effectue auprès du conseil départemental. Il n’y a pas de conditions de revenu pour en bénéficier.",
            icon: FaUsers,
            link: "https://www.service-public.fr/particuliers/vosdroits/N392"
        },
        {
            title: "Allocation aux adultes handicapés (AAH)",
            description: "L’allocation aux adultes handicapés (AAH) est une aide financière qui vous permet d’avoir un minimum de ressources. Elle est accordée sur décision de la CDAPH.",
            icon: FaHandHoldingHeart,
            link: "https://www.service-public.fr/particuliers/vosdroits/F12242"
        },
        {
            title: "Déduction fiscale",
            description: "Les sommes versées au titre de l’aide à domicile à HIS peuvent faire l’objet d’une déduction fiscale à concurrence de 50% des sommes engagées dans la limite de 15 000 €.",
            icon: FaFileInvoice,
            link: "https://www.service-public.fr/particuliers/vosdroits/F823"
        },
        {
            title: "Allocation de solidarité aux personnes âgées (Aspa)",
            description: "L’Aspa est une prestation mensuelle accordée aux retraités ayant de faibles ressources et vivant en France. Son montant dépend de votre situation familiale.",
            icon: FaCalendarAlt,
            link: "https://www.service-public.fr/particuliers/vosdroits/F16871"
        },
        {
            title: "Majoration pour la vie autonome (MVA)",
            description: "La MVA est une aide financière qui peut s’ajouter à l’AAH. Elle permet de faire face aux dépenses liées à votre handicap.",
            icon: FaMoneyBillWave,
            link: "https://www.service-public.fr/particuliers/vosdroits/F12903"
        },
    ];

    const sectionVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <Box className={isDark ? 'bg-[#0a0f16] min-h-screen' : 'bg-[#FAFCFF] min-h-screen'}>
            <Helmet>
                <title>Aides financières pour l'aide à domicile — Hetep Iaout Services</title>
                <meta name="description" content="Découvrez les aides financières pour financer votre maintien à domicile : APA, PCH, crédit d'impôt à 50 %. Hetep Iaout Services vous guide dans toutes vos démarches." />
                <link rel="canonical" href="https://www.hisvie.com/aide-financiere/" />
            </Helmet>

            <div className="w-full">

                {/* --- HERO PREMIUM AÉRÉ --- */}
                <header className="pt-32 pb-16 px-4 md:px-6 max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                        <motion.div 
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="w-full lg:w-1/2 space-y-8"
                        >
                            <div className="space-y-4">
                                <h1 className={`text-5xl md:text-6xl lg:text-[5.5rem] font-black leading-[1.05] tracking-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                    Aides <br />
                                    <span className="text-blue-600 font-serif italic font-light">financières.</span>
                                </h1>
                                <div className="w-20 h-1.5 bg-blue-600 rounded-full"></div>
                            </div>
                            <p className={`text-lg md:text-xl font-light leading-relaxed max-w-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                Comprendre vos droits et les modes de financement disponibles est essentiel pour un accompagnement serein.
                            </p>
                        </motion.div>

                        <motion.div 
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="w-full lg:w-1/2"
                        >
                            <img 
                                src={Image7} 
                                alt="Accompagnement HIS" 
                                className="w-full h-[400px] md:h-[500px] object-cover rounded-[2.5rem] shadow-2xl" 
                            />
                        </motion.div>
                    </div>
                </header>

                <main className="max-w-4xl mx-auto px-6 pb-20">
                    {/* --- LISTE DES AIDES --- */}
                    <div className="space-y-6">
                        {aides.map((aide, index) => (
                            <motion.div 
                                key={index} 
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className={`flex flex-col md:flex-row gap-6 p-8 rounded-3xl border transition-all hover:shadow-md ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-100 shadow-sm'}`}
                            >
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${isDark ? 'bg-gray-800 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                    <aide.icon size={24} />
                                </div>
                                <div className="flex-1">
                                    <h2 className={`text-xl md:text-2xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>{aide.title}</h2>
                                    <p className={`text-base leading-relaxed mb-4 font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{aide.description}</p>
                                    <a href={aide.link} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center font-bold text-sm uppercase tracking-wide hover:underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                        En savoir plus →
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* --- CTA FINAL COMBINÉ : DROITS & CESU --- */}
                    <motion.section 
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
                        className="pt-24"
                    >
                        <div className={`relative overflow-hidden rounded-[2.5rem] p-8 md:p-14 flex flex-col items-center text-center gap-8 border shadow-xl ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                            
                            {/* Élément graphique de fond */}
                            <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl"></div>

                            <div className="max-w-3xl z-10 space-y-6">
                                <h2 className={`text-3xl md:text-5xl font-black tracking-tight leading-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                    Une question sur <span className="text-blue-600 font-serif italic font-light">vos droits</span> ou le <span className="text-blue-600 font-serif italic font-light">CESU ?</span>
                                </h2>
                                <p className={`text-base md:text-xl font-light leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Nos conseillers vous accompagnent pour mobiliser vos aides et acceptent le règlement par <strong>CESU préfinancé</strong> pour faciliter la gestion de vos prestations au quotidien.
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row w-full md:w-auto gap-4 z-10 shrink-0">
                                <Link to="/contact" className="w-full sm:w-auto">
                                    <button className="flex items-center justify-center gap-3 bg-blue-600 text-white px-10 py-4 rounded-2xl font-bold hover:bg-blue-700 transition-all w-full shadow-lg shadow-blue-600/30">
                                        <FaPhoneAlt size={16} /> Nous contacter
                                    </button>
                                </Link>
                                <a href="mailto:his@donnerlavieauxannees.com" className={`flex items-center justify-center gap-3 px-10 py-4 rounded-2xl font-bold transition-all border w-full sm:w-auto ${isDark ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100'}`}>
                                    <FaEnvelope size={16} /> Par Email
                                </a>
                            </div>
                        </div>
                    </motion.section>
                </main>
            </div>
        </Box>
    );
};

export default AideFinanciere;